/**
 * Admin support-tickets API. Backed by moderation-service:
 *
 *   GET  /admin/support-tickets?status=open|in_progress|awaiting_user|resolved|closed&category=…
 *   POST /admin/support-tickets/:id/claim
 *   POST /admin/support-tickets/:id/awaiting
 *   POST /admin/support-tickets/:id/resolve   { resolutionNote, finalStatus }
 *
 * Replies to the user happen in HelpScout (every ticket mirrors there
 * on submit; the externalThreadId is the conversation id). The admin
 * SPA links into HelpScout when a thread id is present.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackAdminEvent } from '../lib/posthog.js';
import { apiFetch } from './client.js';

export interface SupportTicketRow {
  id: string;
  userId: string | null;
  requesterEmail: string;
  category: string;
  subject: string;
  status: string;
  assignedAdminId: string | null;
  slaDeadline: string;
  externalThreadId: string | null;
  createdAt: string;
}

export type SupportTicketStatusFilter =
  | 'open'
  | 'in_progress'
  | 'awaiting_user'
  | 'resolved'
  | 'closed';

export type SupportTicketCategoryFilter =
  | 'general'
  | 'trust_safety'
  | 'payment_refund'
  | 'subscription'
  | 'profile_help'
  | 'matching_help'
  | 'chat_help'
  | 'liveness_help'
  | 'account_recovery';

export const supportTicketKeys = {
  all: ['admin-support-tickets'] as const,
  list: (status: SupportTicketStatusFilter, category: SupportTicketCategoryFilter | null) =>
    [...supportTicketKeys.all, 'list', status, category ?? 'any'] as const,
} as const;

export function useSupportTicketsQuery(
  status: SupportTicketStatusFilter,
  category: SupportTicketCategoryFilter | null,
) {
  return useQuery({
    queryKey: supportTicketKeys.list(status, category),
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('status', status);
      if (category) params.set('category', category);
      params.set('limit', '100');
      return apiFetch<{ tickets: SupportTicketRow[] }>({
        method: 'GET',
        url: `/admin/support-tickets?${params.toString()}`,
      });
    },
    // Tight enough to feel current; long enough that the operator
    // staring at the queue while typing a reply doesn't hammer the
    // backend. Manual Refresh button covers the in-between case.
    staleTime: 30_000,
  });
}

export function useClaimSupportTicketMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ assigned: boolean }>({
        method: 'POST',
        url: `/admin/support-tickets/${encodeURIComponent(id)}/claim`,
      }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: supportTicketKeys.all });
      trackAdminEvent('admin.support_ticket.claimed', { ticketId: id });
    },
  });
}

export function useAwaitingSupportTicketMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ updated: boolean }>({
        method: 'POST',
        url: `/admin/support-tickets/${encodeURIComponent(id)}/awaiting`,
      }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: supportTicketKeys.all });
      trackAdminEvent('admin.support_ticket.awaiting', { ticketId: id });
    },
  });
}

export function useResolveSupportTicketMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      resolutionNote: string;
      finalStatus?: 'resolved' | 'closed';
    }) =>
      apiFetch<{ resolved: boolean }>({
        method: 'POST',
        url: `/admin/support-tickets/${encodeURIComponent(args.id)}/resolve`,
        body: {
          resolutionNote: args.resolutionNote,
          ...(args.finalStatus ? { finalStatus: args.finalStatus } : {}),
        },
      }),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: supportTicketKeys.all });
      trackAdminEvent('admin.support_ticket.resolved', {
        ticketId: args.id,
        finalStatus: args.finalStatus ?? 'resolved',
      });
    },
  });
}
